import { useState } from "react";
import { useDispatch } from "react-redux";
import useInput from "../../hooks/userInput";
import { useNavigate } from "react-router-dom";
import {
  BsPersonFill,
  BsKeyFill,
  BsEyeFill,
  BsEyeSlashFill,
} from "react-icons/bs";
import { HiArrowRightOnRectangle } from "react-icons/hi2";
import { motion } from "framer-motion";

import classes from "./Login.module.css";
import adminUi from "../UI/AdminUi.module.css";
import { logIn, verifyLoginTwoFactor } from "../../api/api";
import { authActions } from "../../store/auth-slice";
import { alertActions } from "../../store/alert-slice";
import Spinner from "../UI/Spinner";
import { setAuthToken } from "../../util/authToken";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [passwordType, setPasswordType] = useState("password");
  const [showSpinner, setShowSpinner] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    value: usernameInput,
    enteredValueIsValid: usernameInputIsValid,
    hasError: usernameInputIsInvalid,
    valueInputChangedHandler: usernameInputChangedHandler,
    valueInputBlurHandler: usernameInputBlurHandler,
  } = useInput((value) => value.trim() !== "");

  const {
    value: passwordInput,
    enteredValueIsValid: passwordInputIsValid,
    hasError: passwordInputIsInvalid,
    valueInputChangedHandler: passwordInputChangedHandler,
    valueInputBlurHandler: passwordInputBlurHandler,
    reset: passwordInputReset,
  } = useInput((value) => value.trim() !== "");

  const switchEyeIcon = () => {
    setShowPassword((initialstate) => !initialstate);
  };

  const switchType = (val) => {
    setPasswordType(val);
  };

  const passwordActionSee = () => {
    switchEyeIcon();
    switchType("text");
  };
  const passwordActioBlind = () => {
    switchEyeIcon();
    switchType("password");
  };

  const isVerifyingCode = !!twoFactorToken;
  const codeIsValid = /^\d{6}$/.test(verificationCode.trim());
  let formIsValid = false;

  if (isVerifyingCode) {
    formIsValid = codeIsValid;
  } else if (usernameInputIsValid && passwordInputIsValid) {
    formIsValid = true;
  }

  const submitHandler = async (e) => {
    e.preventDefault();
    setShowSpinner(true);

    const res = isVerifyingCode
      ? await verifyLoginTwoFactor({
          code: verificationCode.trim(),
          twoFactorToken,
        })
      : await logIn({
          username: usernameInput,
          password: passwordInput,
        });

    if (res.status === "success") {
      if (res.requiresTwoFactor) {
        setTwoFactorToken(res.twoFactorToken);
        dispatch(
          alertActions.setState({ message: res.message, status: res.status })
        );
      } else {
        dispatch(authActions.login({ user: res.data.user }));
        dispatch(
          alertActions.setState({ message: res.message, status: res.status })
        );
        setAuthToken(res.token);
        navigate("/", { replace: true });
      }
    } else {
      dispatch(
        alertActions.setState({ message: res.message, status: "error" })
      );
    }

    setShowSpinner(false);
    if (!isVerifyingCode) {
      passwordInputReset();
    } else {
      setVerificationCode("");
    }
  };

  const usernameInputClasses = usernameInputIsInvalid
    ? `${adminUi.field} ${classes.invalid}`
    : adminUi.field;

  const passwordInputClasses = passwordInputIsInvalid
    ? `${adminUi.field} ${classes.invalid}`
    : adminUi.field;

  return (
    <div className={classes.loginShell}>
      <motion.form
        className={`${adminUi.formCard} ${classes.loginForm}`}
        onSubmit={submitHandler}
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: "spring", stiffness: 320, damping: 28 }}
      >
        {showSpinner && <Spinner />}
        <h1 className={adminUi.formTitle}>
          <HiArrowRightOnRectangle
            className={adminUi.formTitleIcon}
            aria-hidden
          />
          Administrator sign in
        </h1>
        <p className={adminUi.pageLead} style={{ marginBottom: "2rem" }}>
          {isVerifyingCode
            ? "Enter the 6-digit code sent to your email to continue."
            : "Use your admin credentials to manage accounts and transactions."}
        </p>
        {!isVerifyingCode && (
          <div className={usernameInputClasses}>
            <label htmlFor="username">Username</label>
            <div className={classes.inputWrap}>
              <BsPersonFill className={classes.icon} />
              <input
                id="username"
                type="text"
                autoComplete="username"
                value={usernameInput}
                onChange={usernameInputChangedHandler}
                onBlur={usernameInputBlurHandler}
              />
            </div>
          </div>
        )}

        {!isVerifyingCode && (
          <div className={passwordInputClasses}>
            <label htmlFor="password">Password</label>
            <div className={classes.inputWrap}>
              <BsKeyFill className={classes.icon} />
              <input
                id="password"
                type={passwordType}
                autoComplete="current-password"
                value={passwordInput}
                onChange={passwordInputChangedHandler}
                onBlur={passwordInputBlurHandler}
              />
              {!showPassword && (
                <BsEyeFill
                  className={`${classes.icon} ${classes.iconButton}`}
                  onClick={passwordActionSee}
                  role="button"
                  aria-label="Show password"
                />
              )}
              {showPassword && (
                <BsEyeSlashFill
                  className={`${classes.icon} ${classes.iconButton}`}
                  onClick={passwordActioBlind}
                  role="button"
                  aria-label="Hide password"
                />
              )}
            </div>
          </div>
        )}

        {isVerifyingCode && (
          <div className={adminUi.field}>
            <label htmlFor="verificationCode">Verification code</label>
            <div className={classes.inputWrap}>
              <BsKeyFill className={classes.icon} />
              <input
                id="verificationCode"
                type="text"
                inputMode="numeric"
                maxLength={6}
                autoComplete="one-time-code"
                value={verificationCode}
                onChange={(e) =>
                  setVerificationCode(e.target.value.replace(/\D/g, ""))
                }
              />
            </div>
          </div>
        )}
        <div className={adminUi.actions}>
          <button
            className={adminUi.btnPrimary}
            type="submit"
            disabled={!formIsValid}
          >
            <HiArrowRightOnRectangle
              className={adminUi.btnPrimaryIcon}
              aria-hidden
            />
            {isVerifyingCode ? "Verify code" : "Sign in"}
          </button>
        </div>
      </motion.form>
    </div>
  );
};

export default Login;
